import { call } from 'redux-saga/effects';
import { SERVER_ADDRESSES, SERVER_APPLICATION_NAMES } from '../../config/serverAddresses';
import { ActionService } from '../actionService/actionService';
import { apiEndPoints } from '../../config/endPoints/api';
import { generateFilters } from '../../containers/StatPage/sagas';
import type {
  ActivateUserRequestDto,
  ChangeAutopasteRoomPriorityRequestDto,
  ChangeEmailRequestDto,
  ChangePasswordRequestDto,
  ChangeUserAvailablePokerSiteIdsRequestDto,
  ChangeUserRoomsToSearchRequestDto,
  ChangeUserSubscriptionDateRequestDto,
  ChangeUserSubscriptionRequestDto,
  ConfirmRequestDto,
  FindPlayerRequestDto,
  FindPlayersRequestDto,
  GetUsersRequestDto,
  GetParserErrorsRequestDto,
  LoginRequestDto,
  PlayerStatsRequestDto,
  RecordPaymentRequestDto,
  RecordPayoutRequestDto,
  RefreshTokenRequestDto,
  RegisterRequestDto,
  ResetPasswordRequestDto,
  SendFeedbackRequestDto,
  TopPlayersRequestDto,
  UpdateReferralSettingsRequestDto,
  AnalyticsUnit,
  PokerSiteId,
} from '@poker-stats/contracts';

const apiService = new ActionService(SERVER_ADDRESSES.API, 'HTTP', SERVER_APPLICATION_NAMES.API);

export default {
  user: function* () {
    return (yield call([apiService, apiService.Get], apiEndPoints.auth.user)) as ReturnType<
      typeof apiService.Get
    >;
  },
  login: function* (email: string, password: string) {
    return (yield call([apiService, apiService.Post], apiEndPoints.auth.login, {
      email,
      password,
    } satisfies LoginRequestDto)) as ReturnType<typeof apiService.Post>;
  },
  logout: function* () {
    return (yield call([apiService, apiService.Post], apiEndPoints.auth.logout)) as ReturnType<
      typeof apiService.Post
    >;
  },
  register: function* (email: string, password: string, referralCode?: string) {
    return (yield call([apiService, apiService.Post], apiEndPoints.auth.register, {
      email,
      password,
      referralCode,
    } satisfies RegisterRequestDto)) as ReturnType<typeof apiService.Post>;
  },
  changePassword: function* (oldPassword: string, newPassword: string) {
    return (yield call([apiService, apiService.Post], apiEndPoints.auth.changePassword, {
      oldPassword,
      newPassword,
    } satisfies ChangePasswordRequestDto)) as ReturnType<typeof apiService.Post>;
  },
  changeEmail: function* (newEmail: string) {
    return (yield call([apiService, apiService.Post], apiEndPoints.auth.changeEmail, {
      newEmail,
    } satisfies ChangeEmailRequestDto)) as ReturnType<typeof apiService.Post>;
  },
  sendFeedback: function* (subject: string, message: string) {
    return (yield call([apiService, apiService.Post], apiEndPoints.feedback.feedback, {
      subject,
      message,
    } satisfies SendFeedbackRequestDto)) as ReturnType<typeof apiService.Post>;
  },
  reset: function* (email: string) {
    return (yield call([apiService, apiService.Post], apiEndPoints.auth.reset, {
      email,
    } satisfies ResetPasswordRequestDto)) as ReturnType<typeof apiService.Post>;
  },
  confirm: function* (token: string) {
    return (yield call([apiService, apiService.Post], apiEndPoints.auth.confirm, {
      token,
    } satisfies ConfirmRequestDto)) as ReturnType<typeof apiService.Post>;
  },
  refresh: function* (refreshToken: string) {
    return (yield call([apiService, apiService.Post], apiEndPoints.auth.refresh, {
      refreshToken,
    } satisfies RefreshTokenRequestDto)) as ReturnType<typeof apiService.Post>;
  },
  getPlayerGraph: function* (
    playername: string,
    pokersiteId: 1 | 2 | 3 | 4 | 5 | 6,
    filters: ReturnType<typeof generateFilters>,
  ) {
    return (yield call(
      [apiService, apiService.Get],
      apiEndPoints.statistics.graph,
      undefined,
      {
        playername,
        pokersiteId,
        ...filters,
      } satisfies PlayerStatsRequestDto,
      false,
    )) as ReturnType<typeof apiService.Get>;
  },
  getPlayerStats: function* (
    playername: string,
    pokersiteId: 1 | 2 | 3 | 4 | 5 | 6,
    filters: ReturnType<typeof generateFilters>,
  ) {
    return (yield call([apiService, apiService.Get], apiEndPoints.statistics.stats, undefined, {
      playername,
      pokersiteId,
      ...filters,
    } satisfies PlayerStatsRequestDto)) as ReturnType<typeof apiService.Get>;
  },
  getPlayers: function* (playername: string, limit = 50) {
    return (yield call(
      [apiService, apiService.Get],
      apiEndPoints.players.players,
      undefined,
      {
        playername,
        limit,
        smartSearch: localStorage.getItem('smart-search') === 'true',
      } satisfies FindPlayersRequestDto,
      false,
    )) as ReturnType<typeof apiService.Get>;
  },
  getAutopastePlayer: function* (playername: string) {
    return (yield call(
      [apiService, apiService.Get],
      apiEndPoints.players.autopaste,
      undefined,
      {
        playername,
      } satisfies FindPlayerRequestDto,
      false,
    )) as ReturnType<typeof apiService.Get>;
  },
  getTopPlayers: function* (filters: ReturnType<typeof generateFilters>) {
    return (yield call(
      [apiService, apiService.Get],
      apiEndPoints.statistics.topPlayers,
      undefined,
      {
        ...filters,
      } satisfies TopPlayersRequestDto,
    )) as ReturnType<typeof apiService.Get>;
  },
  getUsers: function* (search: string, sortKey?: string, sortOrder?: string) {
    return (yield call([apiService, apiService.Get], apiEndPoints.users.users, undefined, {
      search,
      sortKey,
      sortOrder,
    } satisfies GetUsersRequestDto)) as ReturnType<typeof apiService.Get>;
  },

  getParserErrors: function* (page: number, pageSize: number) {
    return (yield call(
      [apiService, apiService.Get],
      apiEndPoints.parserErrors.parserErrors,
      undefined,
      {
        page,
        pageSize,
      } satisfies GetParserErrorsRequestDto,
      false,
    )) as ReturnType<typeof apiService.Get>;
  },

  uploadMiningFile: function* (pokerSiteId: PokerSiteId, file: File, signal: AbortSignal) {
    return (yield call([apiService, apiService.PostFile], apiEndPoints.mining.upload(pokerSiteId), {
      file,
      signal,
    })) as ReturnType<typeof apiService.PostFile>;
  },

  deleteParserError: function* (id: number) {
    return (yield call(
      [apiService, apiService.Delete],
      apiEndPoints.parserErrors.parserErrors,
      id,
      undefined,
      false,
    )) as ReturnType<typeof apiService.Delete>;
  },

  getAnalyticsOverview: function* (unit: AnalyticsUnit) {
    return (yield call(
      [apiService, apiService.Get],
      apiEndPoints.analytics.overview,
      undefined,
      { unit },
      false,
    )) as ReturnType<typeof apiService.Get>;
  },

  getAnalyticsRooms: function* (unit: AnalyticsUnit) {
    return (yield call(
      [apiService, apiService.Get],
      apiEndPoints.analytics.rooms,
      undefined,
      { unit },
      false,
    )) as ReturnType<typeof apiService.Get>;
  },

  getUser: function* (id: number) {
    return (yield call([apiService, apiService.Get], apiEndPoints.users.users, id)) as ReturnType<
      typeof apiService.Get
    >;
  },

  activateUser: function* (id: number) {
    return (yield call([apiService, apiService.Put], apiEndPoints.users.activate, undefined!, {
      id,
    } satisfies ActivateUserRequestDto)) as ReturnType<typeof apiService.Get>;
  },

  changeUserAvailablePokersiteIds: function* (id: number, availablePokersiteIds: Array<number>) {
    return (yield call(
      [apiService, apiService.Put],
      apiEndPoints.users.changeUserAvailablePokersiteIds,
      undefined!,
      {
        id,
        availablePokersiteIds,
      } satisfies ChangeUserAvailablePokerSiteIdsRequestDto,
    )) as ReturnType<typeof apiService.Get>;
  },

  changeUserSubscription: function* (id: number, subscription?: number) {
    return (yield call(
      [apiService, apiService.Put],
      apiEndPoints.users.changeUserSubscription,
      undefined!,
      {
        id,
        subscription,
      } satisfies ChangeUserSubscriptionRequestDto,
    )) as ReturnType<typeof apiService.Get>;
  },

  changeUserSubscriptionDate: function* (id: number, subscriptionDate?: string) {
    return (yield call(
      [apiService, apiService.Put],
      apiEndPoints.users.changeUserSubscriptionDate,
      undefined!,
      {
        id,
        subscriptionDate,
      } satisfies ChangeUserSubscriptionDateRequestDto,
    )) as ReturnType<typeof apiService.Get>;
  },

  changeUserRoomsToSearch: function* (id: number, roomsToSearch: Array<number>) {
    return (yield call(
      [apiService, apiService.Put],
      apiEndPoints.users.changeUserRoomsToSearch,
      undefined!,
      {
        id,
        roomsToSearch,
      } satisfies ChangeUserRoomsToSearchRequestDto,
    )) as ReturnType<typeof apiService.Put>;
  },

  changeAutopasteRoomPriority: function* (id: number, autopasteRoomPriority: Array<number>) {
    return (yield call(
      [apiService, apiService.Put],
      apiEndPoints.users.changeAutopasteRoomPriority,
      undefined!,
      {
        id,
        autopasteRoomPriority,
      } satisfies ChangeAutopasteRoomPriorityRequestDto,
      undefined,
      false,
    )) as ReturnType<typeof apiService.Put>;
  },

  getReferralSummary: function* () {
    return (yield call([apiService, apiService.Get], apiEndPoints.referrals.me)) as ReturnType<
      typeof apiService.Get
    >;
  },

  getAdminReferrals: function* () {
    return (yield call([apiService, apiService.Get], apiEndPoints.referrals.admin)) as ReturnType<
      typeof apiService.Get
    >;
  },

  recordReferralPayment: function* (params: RecordPaymentRequestDto) {
    return (yield call(
      [apiService, apiService.Post],
      apiEndPoints.referrals.payment,
      params,
    )) as ReturnType<typeof apiService.Post>;
  },

  updateReferralSettings: function* (referralPercent: number) {
    return (yield call([apiService, apiService.Put], apiEndPoints.referrals.settings, undefined!, {
      referralPercent,
    } satisfies UpdateReferralSettingsRequestDto)) as ReturnType<typeof apiService.Put>;
  },

  deleteReferralPayment: function* (id: number) {
    return (yield call(
      [apiService, apiService.Delete],
      apiEndPoints.referrals.payment,
      id,
    )) as ReturnType<typeof apiService.Delete>;
  },

  recordReferralPayout: function* (params: RecordPayoutRequestDto) {
    return (yield call(
      [apiService, apiService.Post],
      apiEndPoints.referrals.payout,
      params,
    )) as ReturnType<typeof apiService.Post>;
  },

  deleteReferralPayout: function* (id: number) {
    return (yield call(
      [apiService, apiService.Delete],
      apiEndPoints.referrals.payout,
      id,
    )) as ReturnType<typeof apiService.Delete>;
  },
};
